import React, { useEffect, useRef, useState } from 'react';
import prefersReducedMotion from './prefersReducedMotion';

const FEATHER = {
  edges: 'pnp-feather',
  soft: 'pnp-feather-soft',
  y: 'pnp-feather-y',
  ui: 'pnp-feather-ui',
  none: '',
};

// Remembers poster probes across mounts so each poster is checked once.
const posterCache = new Map();

function probePoster(url) {
  if (posterCache.has(url)) return posterCache.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
  posterCache.set(url, p);
  return p;
}

/**
 * A muted, looping product video that only downloads and plays while it is
 * on screen, feathered so it dissolves into the --ink canvas.
 *
 * Files: /videos/<name>.mp4 (1080p60), <name>-sm.mp4 (720p30, phones and
 * `small` slots) and <name>.jpg (poster).
 *
 * Degrades gracefully: reduced motion -> poster only; video missing ->
 * poster; poster missing too -> an empty, same-sized slot (nothing shown).
 *
 * `active` (optional) takes over playback: true plays, false pauses
 * (used by the pinned scroll story). Left undefined, it plays in view.
 */
export default function LoopVideo({
  name,
  label,
  small = false,
  feather = 'edges',
  className = '',
  style,
  active,
  loop = true,
  videoRef,
  eager = false,
}) {
  const ref = useRef(null);
  const [reduced] = useState(prefersReducedMotion);
  const [posterOk, setPosterOk] = useState(null); // null = unknown
  const [videoFailed, setVideoFailed] = useState(false);
  const [src, setSrc] = useState(null);
  const poster = `/videos/${name}.jpg`;

  useEffect(() => {
    let alive = true;
    probePoster(poster).then((ok) => { if (alive) setPosterOk(ok); });
    return () => { alive = false; };
  }, [poster]);

  const pickSrc = () => {
    const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    return `/videos/${name}${small || phone ? '-sm' : ''}.mp4`;
  };

  const inView = useRef(false);
  const shouldPlay = () => (active === undefined ? inView.current : !!active);

  // Once a source is attached, start it if it should be playing.
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    if (shouldPlay()) v.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Uncontrolled: load + play while on screen, pause off screen.
  useEffect(() => {
    if (reduced || videoFailed || active !== undefined) return undefined;
    const v = ref.current;
    if (!v) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      inView.current = true;
      setSrc(pickSrc());
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        inView.current = e.isIntersecting;
        if (e.isIntersecting) {
          if (v.getAttribute('src')) v.play().catch(() => {});
          else setSrc(pickSrc());
        } else {
          v.pause();
        }
      },
      { threshold: 0.15, rootMargin: eager ? '200px 0px' : '0px' },
    );
    io.observe(v);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, videoFailed, active, name, small, eager]);

  // Controlled: the parent decides.
  useEffect(() => {
    if (reduced || videoFailed || active === undefined) return;
    const v = ref.current;
    if (!v) return;
    if (active) {
      if (v.getAttribute('src')) v.play().catch(() => {});
      else setSrc(pickSrc());
    } else {
      v.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduced, videoFailed, name, small]);

  const featherClass = FEATHER[feather] || '';
  const cls = `pnp-video ${featherClass} ${className}`.trim();

  if (reduced || videoFailed) {
    if (posterOk === false) return <div className={`${cls} pnp-video-missing`} style={style} aria-hidden="true" />;
    return <img className={cls} style={style} src={poster} alt={label} loading="lazy" decoding="async" onError={() => setPosterOk(false)} />;
  }

  return (
    <video
      ref={(el) => {
        ref.current = el;
        if (videoRef) videoRef.current = el;
      }}
      className={cls}
      style={style}
      poster={posterOk ? poster : undefined}
      src={src || undefined}
      muted
      loop={loop}
      playsInline
      autoPlay={false}
      preload={src ? 'auto' : 'none'}
      aria-label={label}
      onError={() => setVideoFailed(true)}
    />
  );
}
